import { useDispatch, useSelector } from 'react-redux';
import {
  fetchInitialPosts,
  fetchNextPosts,
  fetchPostById,
  toggleLikePost,
  toggleCommentsDisabled,
  handlePollVote,
  setActiveMediaIndex,
  addNewPost,
  fetchPostsByUserId,
  deletePostById,
  updatePostById,
  updateCommentCount,
  fetchNextUserPosts
} from '../redux/postsSlice';
import { fetchCategory } from '../redux/categoriesSlice';
import { store } from '../redux/store';
import { useCallback } from 'react';
import axios from 'axios';
import { CategoriesService, PostsService } from '../services';
import { revertProfileLinks } from "../utils/revertProfileLinks";

export const usePost = () => {
  const dispatch = useDispatch();
  const {
    entities: posts,
    postOrder,
    nextPage,
    hasMore,
    loading,
    singlePostLoading,
    activeMediaIndices = {},
  } = useSelector((state) => state.posts);

  const projectInfo = useSelector((state) => state.projectInfo);
  
  const loadInitialPosts = async (category, sortOption = "latest", page=1) => {
    const fetchPosts = await PostsService.getPosts({
      category_id: category,
      sort: sortOption,
      page,
      per_page: 30
    });
    /**
     * Remove revertProfileLinks when mobile is version is released[]}
     */
    const postsWithParsedContent = fetchPosts.data.data.map(post => ({
      ...post,
      content: revertProfileLinks(post.content)
    }));
    console.log('initialPosts', postsWithParsedContent);
    const result = await dispatch(fetchInitialPosts({ data: postsWithParsedContent }));
    return result.payload;
  };

  const handleImageUploads = useCallback(async (medias, isGif = false) => {

    let pubid = projectInfo?.pubid;
    let channelid = projectInfo?.channelid;
    let token = projectInfo?.accessToken
    let uid = projectInfo?.uid
    if (!pubid || !channelid || !token) {
      console.error("Missing required parameters: pubid, channelid, or token");
      return;
    }
    const url = isGif
      ? `${process.env.REACT_APP_BASE_URL}/post/gif`
      : `${process.env.REACT_APP_BASE_URL}/post/image`;
    const formData = new FormData();
    if (isGif) {
      formData.append("url", medias);
    } else {
      medias.forEach((file) => {
        formData.append(`${file?.name}`, file);
      });
    }
    const customConfig = {
      headers: {
        "Content-Type": "multipart/form-data",
        "Access-Control-Allow-Origin": true,
        crossorigin: true,
        "access-token": token,
        pubid: pubid,
        channelid: channelid,
        uid: uid,
        device_type: "web"
      }
    };

    return await axios.post(
      url, formData,
      customConfig
    );
  }, []);

  const loadMorePosts = useCallback(async (page=2, sortOption, category) => {
    try {
      const postData = await PostsService.getPosts({
        page,
        sort: sortOption,
        category_id: category,
        per_page: 30
      })
      /**
       * Remove revertProfileLinks when mobile is version is released[]}
       */
      const postsWithParsedContent = (postData.data.data || []).map(post => ({
        ...post,
        content: revertProfileLinks(post.content)
      }));
      const result = await dispatch(fetchNextPosts({
        data: { ...postData.data, data: postsWithParsedContent },
        nextPage: page
      }));

      return result.payload; // Return the payload so it can be accessed in the component
    } catch (error) {
      console.error("Error loading more posts:", error);
      return { posts: {}, postOrder: [] };
    }
  }, [dispatch]);

  const getPostById = async (postId) => {
    const response = await PostsService.getPostById(postId);
    /**
     * Remove revertProfileLinks when mobile is version is released[]}
     */
    const post = response.data.data;
    post.content = revertProfileLinks(post.content);
    return await dispatch(fetchPostById({ ...response.data, data: post }));
  };
  
  const updateCommentCountInPost = async (postId, isCommentAdded) => {
    await dispatch(updateCommentCount({
      postId,
      isCommentAdded,
    }));
  }
  const getUserPosts = async (userId, page=1) => {
    if (!userId) return { userPosts: {}, userId };
    try {
      const response = await PostsService?.getPosts({ user_id: userId, page });
      /**
       * Remove revertProfileLinks when mobile is version is released[]}
       */
      const userPosts = (response?.data?.data || []).map(post => ({
        ...post,
        content: revertProfileLinks(post.content)
      }));
      const result = await dispatch(fetchPostsByUserId({ userPosts, userId }));
      // return result.payload || { userPosts, userId };
    } catch (error) {
      console.error("Error fetching user posts:", error);
      return { userPosts: {}, userId };
    }
  };
  const loadMoreUserPosts = async (userId, page) => {
    if (!userId) return { userPosts: {}, userId };
    try {
      const response = await PostsService?.getPosts({ user_id: userId, page });
      /**
       * Remove revertProfileLinks when mobile is version is released[]}
       */
      const userPosts = (response?.data?.data || []).map(post => ({
        ...post,
        content: revertProfileLinks(post.content)
      }));
      await dispatch(fetchNextUserPosts({ userPosts, userId }));
      return userPosts;
    } catch (error) {
      console.error("Error fetching user posts:", error);
      return { userPosts: {}, userId };
    }
  };
  const likePost = async (postId, isLiked) => {
    const is_liked = isLiked ? 0 : 1;
    const response = await PostsService.toggleLike(postId, is_liked);
    await dispatch(toggleLikePost({ postId, isLiked: is_liked }));
  };

  const getLikedUsers = async (postId, page) => {
    const response = await PostsService.getPostLikes(postId, page);
    const likedUsers = response?.data?.data || [];
   
    return likedUsers;

  };

  const togglePostComments = async (postId, currentStatus) => {
    await PostsService.updatePost(postId, {
      comments_disabled: currentStatus ? 0 : 1
    });
    dispatch(toggleCommentsDisabled({ postId, currentStatus }));
  };

  const voteOnPoll = async (postId, updatedOptions) => {
    await dispatch(handlePollVote({ postId, updatedOptions }));
  };

  const setPostMediaIndex = (postId, index) => {
    dispatch(setActiveMediaIndex({ postId, index }));
  };

  const createNewPost = async (data) => {
    const response = await PostsService?.createPost(data);
    const postId = response?.data?.data?.post_id;
    const fetchPostData = await PostsService?.getPostById(postId);
    const newPost = fetchPostData?.data?.data;
    await dispatch(addNewPost({ newPost }));
  };
  const fetchCategories = async () => {
    try {
      // Check if categories are already loaded in Redux store
      const state = store.getState();
      const existingCategories = state.categories.entities;
      
      // If categories are already loaded, return them without making another API call
      if (existingCategories && existingCategories.length > 0) {
        return existingCategories;
      }
      const response = await CategoriesService.getAllCategories();
      const categories = response?.data?.data;

      // Pass the full API response to the fetchCategory action
      await dispatch(fetchCategory({ apiResponse: response.data }));

      
      return categories;
    } catch (error) {
      console.error('Error fetching categories from API:', error);
      return [];
    }
    
  }

  const deletePost = async (postId) => {
    await PostsService.deletePost(postId);
    dispatch(deletePostById({ postId }))
  };

  const updatePost = async (postId, postData) => {
    try {
      await PostsService.updatePost(postId, postData);
      const fetchPostById = await PostsService.getPostById(postId);
      const updatedPost = fetchPostById?.data?.data;
      // Dispatch the update action and get the result
      await dispatch(updatePostById({ postId, updatedPost }));
    } catch (error) {
      console.error("Error updating post:", error);
      return Promise.reject(error);
    }
  };

  return {
    posts,
    postOrder,
    nextPage,
    loading,
    singlePostLoading,
    hasMore,
    activeMediaIndices,
    loadInitialPosts,
    loadMorePosts,
    getPostById,
    likePost,
    getLikedUsers,
    togglePostComments,
    voteOnPoll,
    setPostMediaIndex,
    createNewPost,
    fetchCategories,
    getUserPosts,
    updateCommentCountInPost,
    handleImageUploads,
    deletePost,
    updatePost,
    loadMoreUserPosts
  };
};